import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { MapPin, RefreshCw, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Live Care Feed — anonymized activity in the provider's service area.
 *
 * Providers see: care type + 5-digit zip + coarse time bucket.
 * Providers NEVER see: names, emails, phones, session ids, exact timestamps.
 *
 * Refreshes automatically every 60 seconds and when the tab regains focus.
 */
export default function ProviderCareFeed({ viewAsEmail }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [refreshing, setRefreshing] = useState(false);
  const [activeChips, setActiveChips] = useState([]); // client-side filter

  const load = useCallback(async (isManual = false, isMountedRef = null) => {
    if (isManual) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/provider/care-feed`, {
        params: viewAsEmail ? { as_email: viewAsEmail } : {},
      });
      // Guard against setting state after unmount — a late 60s poll response
      // arriving after the user navigates away would otherwise leak memory.
      if (isMountedRef && !isMountedRef.current) return;
      setState({ loading: false, error: null, data: res.data });
    } catch (err) {
      if (isMountedRef && !isMountedRef.current) return;
      console.error("care-feed load error:", err);
      setState({ loading: false, error: "Couldn't load the feed right now.", data: null });
    } finally {
      if (isMountedRef && !isMountedRef.current) return;
      if (isManual) setRefreshing(false);
    }
  }, [viewAsEmail]);

  useEffect(() => {
    const isMountedRef = { current: true };
    load(false, isMountedRef);
    const interval = setInterval(() => load(false, isMountedRef), 60_000);
    const onFocus = () => load(false, isMountedRef);
    window.addEventListener("focus", onFocus);
    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const events = state.data?.events || [];
  const zipCount = state.data?.zips?.length || 0;

  // Chip list = the union of care-type labels seen in the current feed
  const availableChips = Array.from(
    new Set(events.flatMap(ev => ev.care_types || []))
  ).sort();

  const filteredEvents = activeChips.length === 0
    ? events
    : events.filter(ev => (ev.care_types || []).some(t => activeChips.includes(t)));

  const toggleChip = (chip) => {
    setActiveChips(prev => prev.includes(chip) ? prev.filter(c => c !== chip) : [...prev, chip]);
  };
  const clearChips = () => setActiveChips([]);

  return (
    <section
      data-testid="provider-care-feed-section"
      className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <div className="flex items-center gap-3">
          <div className="relative">
            <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
            <span className="relative block w-3 h-3 rounded-full bg-primary" />
          </div>
          <h2 className="text-2xl font-heading font-semibold text-text-primary">
            Live Care Feed
          </h2>
        </div>
        <Button
          data-testid="care-feed-refresh"
          onClick={() => load(true)}
          variant="ghost"
          size="sm"
          disabled={refreshing}
          className="text-primary hover:bg-primary/5 font-body"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <p className="text-sm text-text-secondary font-body mb-4">
        Anonymous care activity in your service area
        {zipCount > 0 && (
          <> · covering <span className="font-semibold text-text-primary">{zipCount}</span> zip code{zipCount === 1 ? "" : "s"}</>
        )}
      </p>

      {/* Privacy assurance strip */}
      <div className="flex items-start gap-2 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 mb-5">
        <ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
        <p className="text-xs text-text-secondary font-body leading-relaxed">
          For your awareness only — no names, contact info, or exact timestamps are ever shared.
          Reach out through your listing to be found by families searching for care.
        </p>
      </div>

      {/* Care-type filter chips (client-side, only shown when there are events) */}
      {availableChips.length > 0 && (
        <div
          data-testid="care-feed-chips"
          className="flex flex-wrap items-center gap-2 mb-4"
        >
          {availableChips.map((chip) => {
            const active = activeChips.includes(chip);
            return (
              <button
                key={chip}
                type="button"
                data-testid={`care-feed-chip-${chip.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => toggleChip(chip)}
                className={`px-3 py-1 rounded-full text-xs font-body font-semibold border transition-colors ${
                  active
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-primary border-primary/40 hover:bg-primary/5"
                }`}
              >
                {chip}
              </button>
            );
          })}
          {activeChips.length > 0 && (
            <button
              type="button"
              data-testid="care-feed-chips-clear"
              onClick={clearChips}
              className="px-2 py-1 text-xs font-body text-text-secondary hover:text-primary underline"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {state.loading ? (
        <div className="py-8 text-center text-text-secondary font-body text-sm">
          Loading feed…
        </div>
      ) : state.error ? (
        <div
          data-testid="care-feed-error"
          className="py-8 text-center text-red-600 font-body text-sm"
        >
          {state.error}
        </div>
      ) : events.length === 0 ? (
        <div
          data-testid="care-feed-empty"
          className="py-10 text-center border border-dashed border-[#E2E8F0] rounded-xl bg-[#FAFAFA]"
        >
          <Sparkles className="w-8 h-8 text-primary/40 mx-auto mb-2" />
          <p className="font-body font-semibold text-text-primary">Quiet in your area right now</p>
          <p className="text-sm text-text-secondary font-body mt-1">
            When families in your zip codes search for care, you&apos;ll see it here.
          </p>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div
          data-testid="care-feed-filter-empty"
          className="py-8 text-center border border-dashed border-[#E2E8F0] rounded-xl bg-[#FAFAFA]"
        >
          <p className="font-body text-text-secondary text-sm">
            No activity matches your filter. <button onClick={clearChips} className="underline text-primary">Clear filters</button>
          </p>
        </div>
      ) : (
        <ul data-testid="care-feed-list" className="space-y-2">
          {filteredEvents.map((ev) => (
            <li
              key={ev.id}
              data-testid={`care-feed-event-${ev.id}`}
              className="flex items-start justify-between gap-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3 hover:border-primary/40 transition-colors"
            >
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-primary mt-1 shrink-0" />
                <div>
                  <p className="font-body text-sm text-text-primary">
                    Someone in{" "}
                    <span
                      className="font-semibold text-primary"
                      data-testid={`care-feed-zip-${ev.id}`}
                    >
                      {ev.zip_code}
                    </span>{" "}
                    was looking for{" "}
                    <span className="font-semibold text-text-primary">
                      {ev.care_types.length > 0
                        ? ev.care_types.join(", ")
                        : "care options"}
                    </span>
                    .
                  </p>
                  <p className="text-xs text-text-secondary font-body mt-0.5">
                    {ev.when}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
